import debounce from 'lodash/debounce'
import { PlayerState } from '../../../enums'

interface OnCaptionsChangedEvent {
  from: string
  to: string
}

interface OnPauseEvent {
  pauseReason: string
}

interface BeforePlayToResumeEvent {
  playReason: string
}

interface SeekEvent {
  position: number
  offset: number
}

class EventHandlerAdapter {
  private readonly DRAG_TIMELINE_REASON = 'unknown'

  private readonly SEEK_DEBOUNCE_MS = 2000

  private readonly STEP_SECONDS = 10

  private latestPauseReason = ''

  private seekStartEvent: {
    position: number
    offset: number
  } | null = null

  private static previousCaptionsIndex = 0

  constructor(private readonly jwPlayer: jwplayer.JWPlayer) {
    if (!EventHandlerAdapter.previousCaptionsIndex)
      EventHandlerAdapter.previousCaptionsIndex = jwPlayer.getCurrentCaptions()
  }

  onCaptionsChanged =
    (originalEventHandler: (e: OnCaptionsChangedEvent) => void) =>
    ({ track: nextCaptionsIndex }: { track: number }) => {
      const captions: Array<{
        id: string | number
        label: string
        language?: string
      }> = this.jwPlayer.getCaptionsList()

      originalEventHandler({
        from: captions[EventHandlerAdapter.previousCaptionsIndex].label,
        to: captions[nextCaptionsIndex].label,
      })

      EventHandlerAdapter.previousCaptionsIndex = nextCaptionsIndex
    }

  onPause =
    (originalEventHandler: (e: OnPauseEvent) => void) =>
    (event: { pauseReason: string }) => {
      this.latestPauseReason = event.pauseReason

      if (this.pauseDueToTimelineDrag(event.pauseReason)) return

      originalEventHandler(event)
    }

  private readonly pauseDueToTimelineDrag = (pauseReason: string) =>
    pauseReason === this.DRAG_TIMELINE_REASON

  beforePlayToResume =
    (originalEventHandler: (e: BeforePlayToResumeEvent) => void) =>
    (event: { playReason: string }) => {
      if (this.wasPausedDueToTimelineDrag()) return
      if (this.playerIsNotPaused()) return

      originalEventHandler(event)
    }

  private readonly wasPausedDueToTimelineDrag = () =>
    this.latestPauseReason === this.DRAG_TIMELINE_REASON

  private readonly playerIsNotPaused = () =>
    this.jwPlayer.getState() !== PlayerState.PAUSED

  seekToTimelineDragRelease =
    (originalEventHandler: (e: SeekEvent) => void) =>
    (event: { position: number; offset: number }) => {
      const casesWhenSeekShouldRegister =
        this.wasPausedDueToTimelineDrag() || this.playerIsPaused()

      if (casesWhenSeekShouldRegister) {
        if (this.seekStartEvent === null) {
          this.seekStartEvent = event
        }

        //TODO: kan vi alltid kolla på event.position och strunta i this.seekStartEvent.position ?????

        this.debounceSeek(() => {
          originalEventHandler({
            ...event,
            position: this.seekStartEvent
              ? this.seekStartEvent.position
              : event.position,
            offset: event.offset,
          })

          this.seekStartEvent = null
        })
      }
    }

  private readonly playerIsPaused = () =>
    this.jwPlayer.getState() === PlayerState.PAUSED

  private readonly debounceSeek = debounce(
    callback => callback(),
    this.SEEK_DEBOUNCE_MS,
  )

  clickEventToPlayerStepBackward =
    (originalEventHandler: (e: SeekEvent) => void) => () => {
      const newPosition = this.jwPlayer.getPosition()
      const previousPosition = newPosition + this.STEP_SECONDS

      originalEventHandler({ position: previousPosition, offset: newPosition })
    }

  clickEventToPlayerStepForward =
    (originalEventHandler: (e: SeekEvent) => void) => () => {
      const newPosition = this.jwPlayer.getPosition()
      const previousPosition = newPosition - this.STEP_SECONDS

      originalEventHandler({ position: previousPosition, offset: newPosition })
    }
}

export default EventHandlerAdapter
